<template>
  <a-config-provider :locale="locale">
    <div id="app" @click="handleAppAction">
      <component :is="layout">
        <router-view />
      </component>
      <dialog-manager />
      <side-page-manager />
      <window-resize-listener />
      <oc-term />
    </div>
  </a-config-provider>
</template>

<script>
import * as R from 'ramda'
import { mapGetters, mapState } from 'vuex'
import zhCN from 'ant-design-vue/es/locale/zh_CN'
import enUS from 'ant-design-vue/es/locale/en_US'
import DefaultLayout from '@/layouts/Default'
import FullScreenLayout from '@/layouts/FullScreen'
import DialogManager from '@/sections/DialogManager'
import SidePageManager from '@/sections/SidePageManager'
import WindowResizeListener from '@/sections/WindowResizeListener'
import i18n from '@/locales'
import { updateThemeColor } from '@/utils/theme/utils'
import setting from '@/config/setting'
import WindowsMixin from '@/mixins/windows'
import LogoutMixin from '@/mixins/logout'
import {
  getTokenFromCookie,
  decodeToken,
} from '@/utils/auth'
import { SITES } from '@/config/site'

const antdLocales = {
  'zh-CN': zhCN,
  en: enUS,
}

export default {
  name: 'App',
  components: {
    DefaultLayout,
    FullScreenLayout,
    DialogManager,
    SidePageManager,
    WindowResizeListener,
  },
  mixins: [WindowsMixin, LogoutMixin],
  data () {
    return {
      locale: antdLocales[this.$store.getters.setting.language],
    }
  },
  computed: {
    ...mapGetters(['auth', 'theme', 'themeColor', 'scope', 'logo']),
    ...mapState({
      globalSetting: state => state.globalSetting,
      tenant: state => state.auth.tenant,
      session: state => state.auth.auth.session,
    }),
    ...mapState('logo', {
      websiteLogo: state => state.websiteLogo,
      remoteFavicon: state => state.favicon,
    }),
    layout () {
      return `${(this.$route.meta.layout || 'default')}-layout`
    },
    actualLogo () {
      if (this.websiteLogo) {
        return this.websiteLogo
      }
      return this.logo
    },
  },
  watch: {
    'auth.auth' (val, oldVal) {
      if (val && !R.equals(val, oldVal)) {
        if (val.session) {
          this.connect(val.session)
        }
      }
    },
    'globalSetting.value.dictionary': {
      handler (val, oldVal) {
        if (!R.equals(val, oldVal) && !R.isNil(val) && !R.isEmpty(val)) {
          if (!R.isNil(val.en) && !R.isEmpty(val.en)) {
            i18n.mergeLocaleMessage('en', {
              dictionary: val.en,
            })
          }
          if (!R.isNil(val.zh) && !R.isEmpty(val.zh)) {
            i18n.mergeLocaleMessage('zh-CN', {
              dictionary: val.zh,
            })
          }
        }
      },
      immediate: true,
    },
    // remoteFavicon (val) {
    //   console.log(val)
    //   if (val) {
    //     const link = document.querySelector('#favicon')
    //     link.href = this.remoteFavicon
    //   }
    // },
    themeColor: {
      handler (val) {
        if (val && val !== process.env.THEME_COLOR) {
          updateThemeColor(val)
        }
      },
      immediate: true,
    },
  },
  created () {
    this.initIO()
    this.initVisibilityChangeListener()
    this.initMonitorAlertNotify()
    this.initAnnouncementNotify()
    this.initWebsiteLogo()
    this.initUserProfile()
  },
  async mounted () {
    function findSiteByDomain (domain) {
      for (const site in SITES) {
        if (SITES[site].domains.includes(domain) || SITES[site].domains.some(v => domain.endsWith(v))) {
          return site
        }
      }
      return 'LTC' // Or throw some error if you'd like to.
    }

    const getFavicon = async () => {
      const {
        data: { data: [data] },
      } = await this.$http.get('/v1/website_configs?key=favicon&limit=1&sort=id&order=desc')
      return data?.value
    }
    const site = findSiteByDomain(window.location.hostname)
    const link = document.querySelector('#favicon')
    const remoteFavicon = await getFavicon()
    if (remoteFavicon) {
      link.href = remoteFavicon
    } else {
      link.href = `/favicon.${site.toLowerCase()}.ico` // 更换为需要的favicon路径
    }
    this.$store.commit('app/SET_COMPANY_INFO', SITES[site])
  },
  methods: {
    initVisibilityChangeListener () {
      document.addEventListener('visibilitychange', this.handleVisibilityChange)
      this.$once('hook:beforeDestroy', () => {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange)
      })
    },
    handleVisibilityChange () {
      if (document.visibilityState === 'visible') {
        if (
          this.session && this.session !== (decodeToken(getTokenFromCookie()) || {}).session
        ) {
          window.location.reload()
        }
      }
    },
    initIO () {
      if (!this.$appConfig.isPrivate) return
      const options = {}
      if (process.env.NODE_ENV === 'production') {
        options.server = '/'
      }
      // this.notificationListener = notificationListener(this.$store, options)
      // this.socket = this.notificationListener.getSocket()
      // this.connect(this.auth && this.auth.auth && this.auth.auth.session)
    },
    connect (session) {
      // if (!session || !this.socket) return
      // this.socket.io.opts.query.session = session
      // this.socket.connect()
    },
    initUserProfile () {
      this.$store.dispatch('userProfile/get')
    },
    initMonitorAlertNotify () {
      setInterval(() => {
        this.$store.dispatch('monitor/loadMonitorResourceAlerts')
      }, setting.monitorAlertNotifyTriggerTime)
      this.createDialog('MonitorAlertNotifyDialog', {})
    },
    initAnnouncementNotify () {
      this.$store.dispatch('notify/loadAnnouncements')
      setInterval(() => {
        this.$store.dispatch('notify/loadAnnouncements')
      }, 60 * 1000)
    },
    initWebsiteLogo () {
      // this.$store.dispatch('logo/fetchFavicon')
      this.$store.dispatch('logo/fetchWebsiteLogo')
      this.$store.dispatch('logo/fetchTitle')
    },
  },
}
</script>

<style lang="scss" scoped>
#app {
  position: relative;
}
</style>
